import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useApp } from '@/components/AppLayout';
import { X, Box } from 'lucide-react';
import {
  itemApi,
  taxApi,
  itemQuantityLabelApi,
  type Item,
  type CreateItemData,
  type ItemTypeChoice,
  type Tax,
  type TaxTypeChoice,
  type ItemQuantityLabel,
} from '@/lib/api';
import { Checkbox } from '@/components/ui/checkbox';

interface ItemDialogProps {
  open: boolean;
  onClose: () => void;
  businessId: string;
  item?: Item | null;
  onSaved: () => void;
}

export function ItemDialog({ open, onClose, businessId, item, onSaved }: ItemDialogProps) {
  const { currentBusiness } = useApp();
  const isEditing = !!item;

  const [itemTypeChoices, setItemTypeChoices] = useState<ItemTypeChoice[]>([]);
  const [availableTaxes, setAvailableTaxes] = useState<Tax[]>([]);

  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [defaultQuantity, setDefaultQuantity] = useState('1');
  const [rate, setRate] = useState('');
  const [itemType, setItemType] = useState('product_parts');
  const [selectedTaxes, setSelectedTaxes] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Inline tax creation
  const [showAddTaxInline, setShowAddTaxInline] = useState(false);
  const [taxTypeChoices, setTaxTypeChoices] = useState<TaxTypeChoice[]>([]);
  const [newTaxName, setNewTaxName] = useState('');
  const [newTaxRate, setNewTaxRate] = useState('');
  const [newTaxType, setNewTaxType] = useState('exclusive');

  // Quantity label state
  const [quantityLabels, setQuantityLabels] = useState<ItemQuantityLabel[]>([]);
  const [selectedQuantityLabel, setSelectedQuantityLabel] = useState<string | null>(null);
  const [showAddLabelInline, setShowAddLabelInline] = useState(false);
  const [newLabelName, setNewLabelName] = useState('');
  const [newLabelIsDefault, setNewLabelIsDefault] = useState(false);

  // Load choices on mount
  useEffect(() => {
    itemApi.getItemTypes().then((response) => {
      if (response.data) {
        setItemTypeChoices(response.data);
      }
    });

    taxApi.getTaxTypes().then((response) => {
      if (response.data) {
        setTaxTypeChoices(response.data);
      }
    });
  }, []);

  // Load taxes when dialog opens
  useEffect(() => {
    if (open && businessId) {
      loadTaxes();
    }
  }, [open, businessId]);

  // Load quantity labels when itemType changes
  useEffect(() => {
    if (open && businessId && itemType) {
      loadQuantityLabels();
    }
  }, [open, businessId, itemType]);

  // Populate form when editing or reset when adding
  useEffect(() => {
    if (open) {
      if (item) {
        setName(item.name);
        setCode(item.code || '');
        setDescription(item.description || '');
        setDefaultQuantity(item.default_quantity);
        setRate(item.rate);
        setItemType(item.item_type);
        setSelectedTaxes(item.taxes);
        setSelectedQuantityLabel(item.quantity_label);
      } else {
        // Reset form for adding new item
        setName('');
        setCode('');
        setDescription('');
        setDefaultQuantity('1');
        setRate('');
        setItemType('product_parts');
        // Reset quantity label inline state
        setSelectedQuantityLabel(null);
        setShowAddLabelInline(false);
        setNewLabelName('');
        setNewLabelIsDefault(false);
        // Reset tax inline state
        setShowAddTaxInline(false);
        setNewTaxName('');
        setNewTaxRate('');
        setNewTaxType('exclusive');
        // Auto-select taxes based on item type
        taxApi.list(businessId).then((response) => {
          if (response.data) {
            const autoApplyTaxIds = response.data
              .filter((tax) => {
                if (itemType === 'product_parts' && tax.apply_to_all_parts) return true;
                if (itemType === 'labor' && tax.apply_to_all_labor) return true;
                return false;
              })
              .map((tax) => tax.id);
            setSelectedTaxes(autoApplyTaxIds);
          }
        });
      }
      setError('');
    }
  }, [open, item, businessId]);

  const loadTaxes = async () => {
    const response = await taxApi.list(businessId);
    if (response.data) {
      setAvailableTaxes(response.data);
    }
  };

  // Re-evaluate auto-apply taxes when item type changes (only for new items)
  useEffect(() => {
    if (open && !item && businessId) {
      taxApi.list(businessId).then((response) => {
        if (response.data) {
          const autoApplyTaxIds = response.data
            .filter((tax) => {
              if (itemType === 'product_parts' && tax.apply_to_all_parts) return true;
              if (itemType === 'labor' && tax.apply_to_all_labor) return true;
              return false;
            })
            .map((tax) => tax.id);
          setSelectedTaxes(autoApplyTaxIds);
        }
      });
    }
  }, [itemType]);

  const cancelAddTax = () => {
    setShowAddTaxInline(false);
    setNewTaxName('');
    setNewTaxRate('');
    setNewTaxType('exclusive');
  };

  const loadQuantityLabels = async () => {
    const response = await itemQuantityLabelApi.list(businessId, itemType);
    if (response.data) {
      setQuantityLabels(response.data);
    }
  };

  const cancelAddLabel = () => {
    setShowAddLabelInline(false);
    setNewLabelName('');
    setNewLabelIsDefault(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setError('');
    setSubmitting(true);

    let quantityLabelId = selectedQuantityLabel;
    let taxIds = [...selectedTaxes];

    // Create new quantity label if needed
    if (showAddLabelInline && newLabelName) {
      const labelResponse = await itemQuantityLabelApi.create({
        label: newLabelName,
        item_type: itemType,
        is_default: newLabelIsDefault,
        business: businessId,
      });

      if (labelResponse.error) {
        setError(labelResponse.error);
        setSubmitting(false);
        return;
      }

      quantityLabelId = labelResponse.data!.id;
    }

    // Create new tax if needed and add to selected taxes
    if (showAddTaxInline && newTaxName && newTaxRate) {
      const taxResponse = await taxApi.create({
        name: newTaxName,
        rate: newTaxRate,
        tax_type: newTaxType,
        apply_to_all_parts: false,
        apply_to_all_labor: false,
        business: businessId,
      });

      if (taxResponse.error) {
        setError(taxResponse.error);
        setSubmitting(false);
        return;
      }

      taxIds = [...taxIds, taxResponse.data!.id];
    }

    const data: CreateItemData = {
      name,
      code: code || undefined,
      description: description || undefined,
      default_quantity: defaultQuantity,
      rate,
      item_type: itemType,
      taxes: taxIds,
      quantity_label: quantityLabelId,
      business: businessId,
    };

    const response = isEditing
      ? await itemApi.update(item.id, data)
      : await itemApi.create(data);

    setSubmitting(false);

    if (response.error) {
      setError(response.error);
      return;
    }

    onSaved();
    onClose();
  };

  if (!open) return null;

  return (
    <>
      {/* Full-screen overlay */}
      <div className="fixed inset-0 z-50 bg-background flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 flex-shrink-0">
          <div className="flex items-center gap-3">
            <img src="/owl.svg" alt="InvoiceOwl" className="h-8 w-auto" />
            <div className="h-6 w-px bg-border" />
            <span className="font-medium text-sm">{currentBusiness?.name}</span>
          </div>
        </div>

        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto">
          <div className="min-h-full flex flex-col items-center justify-center px-4 py-16">
            {/* Centered content container */}
            <div className="w-full max-w-md">
              <h1 className="text-2xl mb-8 flex items-center gap-2">
                {isEditing ? 'Edit Item' : 'Add Item'}
                <Box className="h-5 w-5 text-blue-400" />
              </h1>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2 group">
                  <Label htmlFor="dialog-name" className="text-muted-foreground group-focus-within:text-foreground transition-colors">Item name</Label>
                  <Input
                    id="dialog-name"
                    placeholder="e.g., Oil Change, Brake Pads"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2 group">
                    <Label htmlFor="dialog-rate" className="text-muted-foreground group-focus-within:text-foreground transition-colors">Unit price</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                      <Input
                        id="dialog-rate"
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        value={rate}
                        onChange={(e) => setRate(e.target.value)}
                        className="pl-7"
                        required
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">Use negative for discounts/coupons</p>
                  </div>

                  <div className="space-y-2 group">
                    <Label htmlFor="dialog-itemType" className="text-muted-foreground group-focus-within:text-foreground transition-colors">Item type</Label>
                    <Select value={itemType} onValueChange={setItemType}>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {itemTypeChoices.map(([value, label]) => (
                          <SelectItem key={value} value={value}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Quantity Label Section */}
                <div className="space-y-2 group">
                  <Label className="text-muted-foreground group-focus-within:text-foreground transition-colors">Quantity label</Label>
                  {showAddLabelInline ? (
                    <div className="flex gap-2 items-center">
                      <div className="flex-1">
                        <Input
                          placeholder="e.g., Hours, Sq Ft, Units"
                          value={newLabelName}
                          onChange={(e) => setNewLabelName(e.target.value)}
                        />
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Checkbox
                          id="set-default-label"
                          checked={newLabelIsDefault}
                          onCheckedChange={(checked) => setNewLabelIsDefault(checked === true)}
                        />
                        <Label htmlFor="set-default-label" className="text-sm text-muted-foreground whitespace-nowrap">
                          Default for {itemTypeChoices.find(([value]) => value === itemType)?.[1] || 'items'}
                        </Label>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          cancelAddLabel();
                          setSelectedQuantityLabel(null);
                        }}
                        className="p-1 text-muted-foreground hover:text-foreground transition-colors shrink-0"
                        aria-label="Remove"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <Select
                      value={selectedQuantityLabel || 'none'}
                      onValueChange={(value) => {
                        if (value === 'add_new') {
                          setShowAddLabelInline(true);
                        } else if (value === 'none') {
                          setSelectedQuantityLabel(null);
                        } else {
                          setSelectedQuantityLabel(value);
                        }
                      }}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="No quantity label" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No quantity label</SelectItem>
                        {quantityLabels.map((label) => (
                          <SelectItem key={label.id} value={label.id}>
                            {label.label} {label.is_default && '(default)'}
                          </SelectItem>
                        ))}
                        <SelectItem value="add_new" className="text-primary">
                          + Add new label
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </div>

                <hr className="border-border" />

                <div className="space-y-2 group">
                  <Label htmlFor="dialog-description" className="text-muted-foreground group-focus-within:text-foreground transition-colors">Description (optional)</Label>
                  <Textarea
                    id="dialog-description"
                    placeholder="Additional details about this item"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={3}
                    className="resize-none"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2 group">
                    <Label htmlFor="dialog-code" className="text-muted-foreground group-focus-within:text-foreground transition-colors">Item code (optional)</Label>
                    <Input
                      id="dialog-code"
                      placeholder="e.g., OC-001"
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                    />
                  </div>

                  <div className="space-y-2 group">
                    <Label htmlFor="dialog-defaultQuantity" className="text-muted-foreground group-focus-within:text-foreground transition-colors">Default quantity</Label>
                    <Input
                      id="dialog-defaultQuantity"
                      type="number"
                      step="0.01"
                      min="0"
                      value={defaultQuantity}
                      onChange={(e) => setDefaultQuantity(e.target.value)}
                    />
                  </div>
                </div>

                {/* Tax Section */}
                <div className="space-y-2 group">
                  <Label className="text-muted-foreground group-focus-within:text-foreground transition-colors">Default taxes</Label>
                  {showAddTaxInline ? (
                    <div className="flex gap-2 items-center">
                      <div className="flex-1">
                        <Input
                          placeholder="Tax name"
                          value={newTaxName}
                          onChange={(e) => setNewTaxName(e.target.value)}
                        />
                      </div>
                      <div className="flex-1">
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          placeholder="Rate %"
                          value={newTaxRate}
                          onChange={(e) => setNewTaxRate(e.target.value)}
                        />
                      </div>
                      <div className="flex-1">
                        <Select value={newTaxType} onValueChange={setNewTaxType}>
                          <SelectTrigger className="w-full">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {taxTypeChoices.map(([value, label]) => (
                              <SelectItem key={value} value={value}>
                                {label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <button
                        type="button"
                        onClick={cancelAddTax}
                        className="p-1 text-muted-foreground hover:text-foreground transition-colors"
                        aria-label="Cancel"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : availableTaxes.length > 0 ? (
                    <div className="space-y-2">
                      {availableTaxes.map((tax) => (
                        <div key={tax.id} className="flex items-center gap-2">
                          <Checkbox
                            id={`tax-${tax.id}`}
                            checked={selectedTaxes.includes(tax.id)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setSelectedTaxes([...selectedTaxes, tax.id]);
                              } else {
                                setSelectedTaxes(selectedTaxes.filter((id) => id !== tax.id));
                              }
                            }}
                          />
                          <Label htmlFor={`tax-${tax.id}`} className="font-normal cursor-pointer">
                            {tax.name} ({tax.rate}%)
                          </Label>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => setShowAddTaxInline(true)}
                        className="text-sm text-primary hover:underline"
                      >
                        + Add new tax
                      </button>
                    </div>
                  ) : (
                    <div className="text-sm text-muted-foreground">
                      <p>No taxes configured.</p>
                      <button
                        type="button"
                        onClick={() => setShowAddTaxInline(true)}
                        className="text-primary hover:underline"
                      >
                        + Add new tax
                      </button>
                    </div>
                  )}
                </div>

                {error && (
                  <p className="text-sm text-destructive">{error}</p>
                )}

                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={onClose}
                    className="flex-1 rounded-full"
                  >
                    Go Back
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 rounded-full"
                    disabled={submitting || !name || !rate}
                  >
                    {submitting
                      ? isEditing ? 'Saving...' : 'Creating...'
                      : isEditing ? 'Save Changes' : 'Create Item'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
